import React, { useEffect, useState } from "react";
import { FaPlus, FaSave, FaEdit, FaTrash } from "react-icons/fa";
import Dialog from "../../FormDialog/Dialog";
import Dropdown from "../../../DropDowns/DropDown";
import MethodAccessGrid from "./MethodAccessGrid";
import {
  MethodAccessGridValue,
  defaultMethodAccess,
  normalizePoPath,
  toEntries,
} from "./permissionObjectUtils";

export interface PoPayload {
  permission_object_name: string;
  po_type_code: string;
  po_path: string;
  service_name: string | null;
  description: string;
  asset_id: number | null;
  access_level_name: string;
  enabled: string;
}

export const BLANK_GRID_MESSAGE =
  "Set at least one method, or leave the defaults (GET read, POST write, PUT write, PATCH write, DELETE admin).";

export interface UsedBy {
  groups: string[];
  roles: { role: string; group: string; access: string }[];
}

interface Props {
  selectedPo: any | null;
  methodAccess: MethodAccessGridValue | null;
  usedBy: UsedBy;
  services: string[];
  permissionObjects: any[];
  onCreate: (payload: PoPayload, grid: MethodAccessGridValue) => Promise<false | "created" | "partial">;
  onUpdate: (id: number, payload: PoPayload, grid: MethodAccessGridValue) => Promise<boolean>;
  onDelete: (id: number) => void;
  onDirtyChange: (dirty: boolean) => void;
  onModeChange: (mode: "create" | "edit" | "view" | null) => void;
  canDelete?: boolean;
}

type Mode = "empty" | "view" | "edit" | "create";

const TYPES = ["API", "UI_ELEMENT", "BLOB_FOLDER"];
const label = "block text-gray-700 mb-2 font-medium";
const inputClass = (ro: boolean) =>
  `w-full p-3 border rounded-lg text-sm ${ro ? "bg-[#f9f9f9] border-gray-300" : "border-blue-500"}`;
const btn = "flex items-center gap-2 px-4 py-2 bg-[#a6a9ac] text-white rounded-lg hover:bg-[#474747]";
// The shared Dropdown in a form field, as UserForm uses it.
const dropdownClass = (ro: boolean) =>
  `w-full p-1 border rounded-lg shadow-inner text-sm ${ro ? "bg-[#f9f9f9] border-gray-300" : "border-blue-500"}`;
const TYPE_OPTIONS = TYPES.map((t) => ({ value: t, label: t }));
const ENABLED_OPTIONS = [
  { value: "Y", label: "Yes" },
  { value: "N", label: "No (disabled)" },
];

const PermissionObjectForm: React.FC<Props> = ({
  selectedPo,
  methodAccess,
  usedBy,
  services,
  permissionObjects,
  onCreate,
  onUpdate,
  onDelete,
  onDirtyChange,
  onModeChange,
  canDelete = false,
}) => {
  const [mode, setMode] = useState<Mode>("empty");
  const [name, setName] = useState("");
  const [type, setType] = useState("API");
  const [path, setPath] = useState("");
  const [service, setService] = useState("");
  const [description, setDescription] = useState("");
  const [enabled, setEnabled] = useState("Y");
  const [grid, setGrid] = useState<MethodAccessGridValue>(defaultMethodAccess("API"));
  const [formError, setFormError] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  const readOnly = mode === "view" || mode === "empty";
  const blankApiGrid = type === "API" && toEntries(grid).length === 0;
  const methodAccessLoading =
    !!selectedPo && selectedPo.po_type_code === "API" && methodAccess === null;
  const seedGrid = () =>
    methodAccess || defaultMethodAccess(selectedPo?.po_type_code === "API" ? "API" : "UI_ELEMENT");
  const changeMode = (m: Mode) => {
    setMode(m);
    onModeChange(m === "empty" ? null : m);
  };

  // Load the selected object (not while the user is editing it).
  useEffect(() => {
    if (selectedPo && mode !== "edit") {
      setName(selectedPo.permission_object_name || "");
      setType(selectedPo.po_type_code || "API");
      setPath(selectedPo.po_path || "");
      setService(selectedPo.service_name || "");
      setDescription(selectedPo.description || "");
      setEnabled(selectedPo.enabled || "Y");
      setFormError("");
      changeMode("view");
      onDirtyChange(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPo]);

  useEffect(() => {
    if (selectedPo && mode !== "edit" && mode !== "create") {
      setGrid(seedGrid());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methodAccess, selectedPo]);

  const touch = () => onDirtyChange(true);

  const startCreate = () => {
    setName("");
    setType("API");
    setPath("");
    setService("");
    setDescription("");
    setEnabled("Y");
    setGrid(defaultMethodAccess("API"));
    setFormError("");
    changeMode("create");
    onDirtyChange(true);
  };

  const validate = (): boolean => {
    if (!name.trim()) return setFormError("Name is required"), false;
    if (!path.trim()) return setFormError("Path is required"), false;
    if (type === "API" && !service) return setFormError("Service is required for API objects"), false;
    const dup = permissionObjects.some(
      (p) =>
        p.po_type_code === type &&
        p.po_path === path.trim() &&
        p.permission_object_id !== selectedPo?.permission_object_id
    );
    if (mode === "create" && dup) return setFormError("A permission object with this type and path already exists"), false;
    setFormError("");
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    if (blankApiGrid) return;
    const payload: PoPayload = {
      permission_object_name: name.trim(),
      po_type_code: type,
      po_path: path.trim(),
      service_name: service || null,
      description,
      asset_id: selectedPo?.asset_id ?? null,
      access_level_name: selectedPo?.access_level_name || "read",
      enabled,
    };
    if (mode === "edit" && selectedPo) {
      if (await onUpdate(selectedPo.permission_object_id, payload, grid)) {
        onDirtyChange(false);
        changeMode("view");
      }
      return;
    }
    const result = await onCreate(payload, grid);
    if (result === "created") {
      onDirtyChange(false);
      setName("");
      changeMode("empty");
    }
    // "partial": the setup page selects the created object, which moves the form to view mode.
  };

  const cancelEdit = () => {
    if (selectedPo) {
      setName(selectedPo.permission_object_name || "");
      setType(selectedPo.po_type_code || "API");
      setPath(selectedPo.po_path || "");
      setService(selectedPo.service_name || "");
      setDescription(selectedPo.description || "");
      setEnabled(selectedPo.enabled || "Y");
      setGrid(seedGrid());
      changeMode("view");
    } else {
      changeMode("empty");
    }
    setFormError("");
    onDirtyChange(false);
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md font-poppins">
      {confirmDelete && selectedPo && (
        <Dialog
          title="Confirm Delete"
          message={
            usedBy.groups.length > 0
              ? `This object is in ${usedBy.groups.length} group(s) (${usedBy.groups.join(", ")}). Delete it anyway?`
              : "Are you sure you want to delete this permission object?"
          }
          confirmButtonText="Yes"
          cancelButtonText="No"
          onConfirm={() => {
            setConfirmDelete(false);
            onDelete(selectedPo.permission_object_id);
            changeMode("empty");
          }}
          onCancel={() => setConfirmDelete(false)}
          onClose={() => setConfirmDelete(false)}
        />
      )}

      <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
        <div className="flex justify-between items-center gap-4 mb-4">
          <div>
            {canDelete && mode === "edit" && (
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white font-medium rounded-lg hover:bg-red-600"
              >
                <FaTrash className="w-4 h-4" /> Delete
              </button>
            )}
          </div>
          {canDelete && (
            <div className="flex gap-4">
              {(mode === "empty" || mode === "view") && (
                <button type="button" onClick={startCreate} className={btn}>
                  <FaPlus /> New
                </button>
              )}
              {mode === "view" && (
                <button
                  type="button"
                  disabled={methodAccessLoading}
                  onClick={() => {
                    changeMode("edit");
                  }}
                  className={`${btn} disabled:opacity-50`}
                >
                  <FaEdit /> Edit
                </button>
              )}
              {(mode === "edit" || mode === "create") && (
                <>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={blankApiGrid}
                    className={`${btn} disabled:opacity-50`}
                  >
                    {mode === "create" ? <FaPlus /> : <FaSave />} {mode === "create" ? "Create" : "Save"}
                  </button>
                  <button type="button" onClick={cancelEdit} className={btn}>
                    Cancel
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {formError && <div role="alert" className="text-sm text-red-600">{formError}</div>}
        {blankApiGrid && !readOnly && (
          <div role="alert" className="text-sm text-red-600">{BLANK_GRID_MESSAGE}</div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <label className={label}>Name<span className="text-red-500 ml-1">*</span></label>
            <input
              className={inputClass(readOnly)}
              value={name}
              disabled={readOnly}
              onChange={(e) => { setName(e.target.value); touch(); }}
            />
          </div>
          <div>
            <label className={label}>Type</label>
            <Dropdown
              ariaLabel="Type"
              options={TYPE_OPTIONS}
              selectedValue={type}
              disabled={mode !== "create"}
              onValueChange={(v) => {
                // react-select also fires on re-picking the selected Type; that must
                // not reset the method grid.
                if (v === type) return;
                setType(v);
                setGrid(defaultMethodAccess(v));
                touch();
              }}
              placeholder="Select Type"
              className={dropdownClass(mode !== "create")}
            />
          </div>
        </div>

        <div>
          <label className={label}>Path<span className="text-red-500 ml-1">*</span></label>
          <input
            className={inputClass(readOnly)}
            value={path}
            disabled={readOnly}
            onChange={(e) => { setPath(e.target.value); touch(); }}
          />
          {!readOnly && type === "API" && normalizePoPath(path) !== path && (
            <p className="text-xs text-amber-600 mt-1">
              Requests are matched on the exact path with ids removed. Store this as{" "}
              <code>{normalizePoPath(path)}</code>.
              {canDelete && (
                <button
                  type="button"
                  className="ml-2 underline font-medium"
                  onClick={() => { setPath(normalizePoPath(path)); touch(); }}
                >
                  Use <code>{normalizePoPath(path)}</code>
                </button>
              )}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <label className={label}>Service</label>
            <Dropdown
              ariaLabel="Service"
              options={[{ value: "", label: "None" }, ...services.map((s) => ({ value: s, label: s }))]}
              selectedValue={service}
              disabled={readOnly}
              onValueChange={(v) => { setService(v); touch(); }}
              placeholder="Select Service"
              className={dropdownClass(readOnly)}
            />
          </div>
          <div>
            <label className={label}>Enabled</label>
            <Dropdown
              ariaLabel="Enabled"
              options={ENABLED_OPTIONS}
              selectedValue={enabled}
              disabled={readOnly}
              onValueChange={(v) => { setEnabled(v); touch(); }}
              placeholder="Select Enabled"
              className={dropdownClass(readOnly)}
            />
          </div>
        </div>

        <div>
          <label className={label}>Description</label>
          <textarea
            rows={2}
            className={inputClass(readOnly)}
            value={description}
            disabled={readOnly}
            onChange={(e) => { setDescription(e.target.value); touch(); }}
          />
        </div>

        {type === "API" && mode !== "empty" && (
          <div>
            <label className={label}>Method access</label>
            <MethodAccessGrid
              value={grid}
              readOnly={readOnly}
              onChange={(v) => { setGrid(v); touch(); }}
            />
          </div>
        )}

        {selectedPo && (
          <div>
            <label className={label}>Used by</label>
            <div className="bg-gray-50 p-4 rounded-md border border-gray-300 text-sm">
              {usedBy.groups.length === 0 ? (
                <span className="text-gray-500">Not in any group.</span>
              ) : (
                <>
                  <div><strong>Groups:</strong> {usedBy.groups.join(", ")}</div>
                  {usedBy.roles.length > 0 && (
                    <div className="mt-1">
                      <strong>Roles:</strong>{" "}
                      {usedBy.roles.map((r) => `${r.role} (${r.access})`).join(", ")}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default PermissionObjectForm;
